import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const phoneAlt = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim() || '';

    if (phoneAlt) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rg3v7nahb7382ovm0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 看片网站推荐2025',
    description: '看片推荐是一个专业的在线观影平台推荐与测评网站，致力于为影迷提供专业、客观、安全的看片网站评测。',
    url: typeof window !== 'undefined' ? window.location.href : '',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <Navbar />
      <main>
        <section className="w-full px-4 md:px-6 pt-24 pb-8 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-6">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">
              看片推荐（KanPianTuiJian）成立于2024年，是一个专注于在线观影平台推荐与测评的专业内容平台。我们致力于为全国影迷提供最全面、最客观、最及时的在线看片网站测评信息，帮助每一位用户找到最适合自己的观影平台。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {[
                { icon: 'ri-eye-line', title: '专业测评', desc: '200+平台深度评测，覆盖8大分类，每个平台均经过实际体验。' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '仅推荐正版合法平台，提供详细的安全访问指南和隐私保护建议。' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为核心，提供客观公正的评分和多维度对比分析。' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-xl bg-background-100">
                  <span className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="text-base font-bold text-foreground-900 mb-2 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
              我们的口碑
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {[
                { num: '100万+', label: '月访问用户' },
                { num: '200+', label: '深度测评报告' },
                { num: '98%', label: '用户满意度' },
                { num: '50万+', label: '收藏用户数' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600 font-heading mb-1">
                    {stat.num}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8">
                用户评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { name: '张先生', role: '影视爱好者', text: '这个网站太棒了！之前总是在各个平台之间纠结，看了测评之后终于找到了最适合我的组合方案。推荐的标准方案确实性价比很高！' },
                  { name: '李女士', role: '家庭用户', text: '作为有两个孩子的妈妈，我需要找适合全家观看的平台。看片推荐的安全指南和平台分类功能帮了大忙，现在全家人都很满意。' },
                  { name: '王同学', role: '大学生', text: '学生党预算有限，看完免费平台的测评文章后发现央视网和B站的免费内容比我想象的丰富很多，省了不少会员费！' },
                  { name: '赵先生', role: '海外用户', text: '长期在国外工作，一直想找办法看国内的影视内容。看片推荐的海外平台和VPN安全指南非常实用，终于可以安心追剧了。' },
                ].map((review) => (
                  <div key={review.name} className="p-6 rounded-xl bg-background-100">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 font-bold text-sm">
                        {review.name.charAt(0)}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">contact@kanpiantuijian.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-600">看片推荐KanPian</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl bg-secondary-200 text-secondary-700">
                      <i className="ri-weibo-line text-lg"></i>
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">新浪微博</h4>
                      <p className="text-sm text-foreground-600">@看片推荐官方</p>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form=""
                    className="bg-background-100 rounded-xl p-6"
                    noValidate
                  >
                    <h3 className="text-base font-bold text-foreground-900 mb-5 font-heading">
                      发送留言
                    </h3>

                    <div className="honeypot-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（不超过500字）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        发送留言
                      </button>
                    </div>

                    {formStatus === 'success' && (
                      <p className="mt-4 text-sm text-green-600 text-center">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="mt-4 text-sm text-red-500 text-center">{formMessage}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}